import { useQuery } from '@tanstack/react-query';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import api from '../../services/api';
import { parsePatientId } from './usePatientBasicInfo';

export interface PatientOrdersData {
  orders: any[];
  activeOrders: any[];
  recentOrders: any[];
}

/**
 * Hook to fetch patient orders
 * Only fetches when Orders tab is active
 */
export function usePatientOrders(
  patientId: string | undefined,
  options?: { enabled?: boolean; limit?: number }
) {
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);
  const { pharmacyId, compositeId } = parsePatientId(patientId, activePharmacy?.id);

  const enabled = options?.enabled !== false && !!pharmacyId && !!compositeId;
  const limit = options?.limit || 50;

  return useQuery({
    queryKey: ['patient', 'orders', compositeId, limit],
    queryFn: async (): Promise<PatientOrdersData> => {
      if (!pharmacyId || !compositeId) {
        throw new Error('Invalid patient ID');
      }

      const response = await api.get(`/api/orders/${pharmacyId}`, {
        params: {
          patientId: compositeId,
          limit
        }
      });

      const orderData = response.data?.data || [];

      // Filter orders to only include this patient's orders
      const patientOrders = orderData.filter((o: any) => {
        const orderPatientId = o.patientId || o.patientCompositeId;
        return orderPatientId === compositeId || orderPatientId === patientId;
      });

      const activeOrders = patientOrders.filter((o: any) =>
        !['delivered', 'completed', 'cancelled'].includes(o.status)
      );

      return {
        orders: patientOrders,
        activeOrders,
        recentOrders: patientOrders.slice(0, 10)
      };
    },
    enabled,
    staleTime: 1 * 60 * 1000, // 1 minute - orders change frequently
  });
}
